import React, { useState } from 'react';
import { useAppStore } from '../../../../store/useAppStore';
import AssetPicker from '../../../Assets/AssetPicker';
import { X } from 'lucide-react';

export const CarouselForm = ({ component, onUpdate }) => {
  const { appName } = useAppStore();
  const [showAddSlide, setShowAddSlide] = useState(false);

  const handleChange = (field, value) => {
    onUpdate(component.id, {
      specificAttrs: { ...component.specificAttrs, [field]: value }
    });
  };

  const { specificAttrs } = component;
  
  // Parse slides from comma-separated string or array
  const getSlides = () => {
    if (!specificAttrs.slides) return [];
    if (Array.isArray(specificAttrs.slides)) return specificAttrs.slides;
    return specificAttrs.slides.split(',').map(s => s.trim()).filter(s => s);
  };

  const slides = getSlides();

  const addSlide = (url) => {
    const updatedSlides = [...slides, url];
    handleChange('slides', updatedSlides.join(','));
    setShowAddSlide(false);
  };

  const removeSlide = (index) => {
    const updatedSlides = slides.filter((_, i) => i !== index);
    handleChange('slides', updatedSlides.join(','));
  };

  const updateSlide = (index, url) => {
    const updatedSlides = [...slides];
    updatedSlides[index] = url;
    handleChange('slides', updatedSlides.join(','));
  };

  return (
    <div style={{ display: 'grid', gap: '12px' }}>
      {/* Carousel Slides */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>
            Carousel Slides
          </label>
          <button
            type="button"
            onClick={() => setShowAddSlide(!showAddSlide)}
            style={{
              padding: '4px 12px',
              fontSize: '11px',
              fontWeight: '600',
              backgroundColor: '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            {showAddSlide ? 'Cancel' : '+ Add Slide'}
          </button>
        </div>

        {/* Add New Slide Section */}
        {showAddSlide && appName && (
          <div style={{
            padding: '12px',
            backgroundColor: '#F0F9FF',
            border: '1px solid #BFDBFE',
            borderRadius: '8px',
            marginBottom: '12px'
          }}>
            <AssetPicker
              appId={appName}
              value=""
              onChange={addSlide}
              label="Select Image for Slide"
              fileType="image"
              placeholder="Choose from library..."
              showPreview={true}
            />
          </div>
        )}

        {/* Existing Slides */}
        {slides.length === 0 ? (
          <div style={{
            padding: '20px',
            backgroundColor: '#F9FAFB',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            textAlign: 'center',
            color: '#6B7280',
            fontSize: '12px'
          }}>
            No slides added yet. Click "+ Add Slide" to begin.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '8px' }}>
            {slides.map((slide, index) => (
              <div
                key={index}
                style={{
                  padding: '8px',
                  backgroundColor: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {/* Slide Preview */}
                <div style={{
                  width: '60px',
                  height: '40px',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  backgroundColor: '#E5E7EB',
                  flexShrink: 0
                }}>
                  <img
                    src={slide}
                    alt={`Slide ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>

                {/* Slide URL Input */}
                <div style={{ flex: 1 }}>
                  <input
                    type="text"
                    value={slide}
                    onChange={(e) => updateSlide(index, e.target.value)}
                    placeholder="Image URL"
                    style={{
                      width: '100%',
                      padding: '4px 8px',
                      fontSize: '11px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '4px'
                    }}
                  />
                </div>

                {/* Slide Number */}
                <span style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#6B7280',
                  minWidth: '20px',
                  textAlign: 'center'
                }}>
                  #{index + 1}
                </span>

                {/* Remove Button */}
                <button
                  type="button"
                  onClick={() => removeSlide(index)}
                  style={{
                    padding: '4px',
                    backgroundColor: '#FEE2E2',
                    color: '#DC2626',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <X style={{ width: '14px', height: '14px' }} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Carousel Options */}
      <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '12px' }}>
        <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '8px' }}>
          Display Options
        </label>
        
        <div style={{ display: 'grid', gap: '8px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={specificAttrs.showArrows !== false}
              onChange={(e) => handleChange('showArrows', e.target.checked)}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '12px', fontWeight: '500', color: '#4B5563' }}>
              Show Navigation Arrows
            </span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={specificAttrs.showIndicators !== false}
              onChange={(e) => handleChange('showIndicators', e.target.checked)}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '12px', fontWeight: '500', color: '#4B5563' }}>
              Show Slide Indicators
            </span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={specificAttrs.autoplay || false}
              onChange={(e) => handleChange('autoplay', e.target.checked)}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '12px', fontWeight: '500', color: '#4B5563' }}>
              Autoplay
            </span>
          </label>
        </div>

        {specificAttrs.autoplay && (
          <div style={{ marginTop: '12px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px', color: '#4B5563' }}>
              Autoplay Interval (seconds)
            </label>
            <input
              type="number"
              value={specificAttrs.interval || 3}
              onChange={(e) => handleChange('interval', parseInt(e.target.value) || 3)}
              min="1"
              max="10"
              style={{
                width: '100%',
                padding: '6px 10px',
                fontSize: '13px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px'
              }}
            />
          </div>
        )}
      </div>

      {/* Info Box */}
      <div style={{
        padding: '10px',
        backgroundColor: '#FEF3C7',
        border: '1px solid #FCD34D',
        borderRadius: '6px',
        fontSize: '11px',
        color: '#92400E'
      }}>
        <strong>Tip:</strong> Add multiple images to create a slideshow. Drag to reorder slides (coming soon).
      </div>
    </div>
  );
};



